import {
  CanvasTexture,
  ClampToEdgeWrapping,
  RepeatWrapping,
  SRGBColorSpace,
  TextureLoader,
  type Texture,
} from 'three'
import { config, type BandId } from '../config'

/** 拼接长条贴图中单帧 cell 的像素尺寸（图片填满整格，无黑边） */
const CELL_W = 320
const CELL_H = 320
/** 内容区填满整格 */
const FRAME_CONTENT_H = CELL_H
const FRAME_CONTENT_Y = 0

/** 生成一张带帧号 + 胶片齿孔的占位 CanvasTexture（图片缺失时使用） */
export function makePlaceholderTexture(label: string, accent = '#b8835a'): CanvasTexture {
  const w = 320
  const h = 240
  const c = document.createElement('canvas')
  c.width = w
  c.height = h
  const ctx = c.getContext('2d')!
  // 主背景
  ctx.clearRect(0, 0, w, h)
  ctx.fillStyle = 'rgba(26, 22, 20, 0.55)'
  ctx.fillRect(0, 0, w, h)
  // 帧内"画面"区
  const margin = 18
  const fw = w - margin * 2
  const fh = h - margin * 2
  const grad = ctx.createLinearGradient(margin, margin, margin + fw, margin + fh)
  grad.addColorStop(0, '#2a2420')
  grad.addColorStop(1, '#3a302a')
  ctx.fillStyle = grad
  ctx.fillRect(margin, margin, fw, fh)
  // 标签
  ctx.fillStyle = accent
  ctx.font = 'bold 28px ui-monospace, Menlo, monospace'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(label, w / 2, h / 2)
  // 胶片齿孔（上下两排）
  const holeY1 = 4
  const holeY2 = h - 14
  const holeH = 10
  for (let i = 0; i < 10; i++) {
    const x = 8 + i * 32
    ctx.fillStyle = '#f5efe2'
    ctx.fillRect(x, holeY1, 12, holeH)
    ctx.fillRect(x, holeY2, 12, holeH)
  }
  const tex = new CanvasTexture(c)
  tex.colorSpace = SRGBColorSpace
  tex.needsUpdate = true
  return tex
}

/** 把一张已有 Texture 做一次 canvas 预模糊；返回新的 CanvasTexture */
export function preBlurTexture(src: Texture, blurPx: number): CanvasTexture {
  const img = src.image as HTMLImageElement | HTMLCanvasElement | ImageBitmap | undefined
  if (!img) return src as unknown as CanvasTexture
  const w = (img as any).width ?? 320
  const h = (img as any).height ?? 240
  const c = document.createElement('canvas')
  c.width = w
  c.height = h
  const ctx = c.getContext('2d')!
  // canvas filter 在 Safari < 14.1 无效；用 try/catch 兜底
  try {
    ctx.filter = `blur(${blurPx}px)`
  } catch {
    /* ignore */
  }
  ctx.drawImage(img as CanvasImageSource, 0, 0, w, h)
  const out = new CanvasTexture(c)
  out.colorSpace = SRGBColorSpace
  out.needsUpdate = true
  return out
}

/** 按需加载某一 band 的所有源帧；缺图自动占位；blurPx > 0 时做预模糊 */
export async function loadBandFrames(
  band: BandId,
  blurPx: number,
): Promise<Texture[]> {
  const sources = config.filmstrip.frameSources
  const loader = new TextureLoader()

  const results = await Promise.allSettled(
    Array.from(
      { length: sources },
      (_, i) =>
        new Promise<Texture>((resolve, reject) => {
          loader.load(
            config.assets.paths.filmFrame(band, i),
            (tex) => {
              tex.colorSpace = SRGBColorSpace
              resolve(tex)
            },
            undefined,
            reject,
          )
        }),
    ),
  )

  return results.map((r, i) => {
    if (r.status === 'fulfilled') {
      const tex = r.value
      return blurPx > 0 ? preBlurTexture(tex, blurPx) : tex
    }
    return makePlaceholderTexture(`${band.toUpperCase()}-${String(i + 1).padStart(2, '0')}`)
  })
}

function configureStripTexture(tex: CanvasTexture): CanvasTexture {
  tex.wrapS = RepeatWrapping
  tex.wrapT = ClampToEdgeWrapping
  tex.colorSpace = SRGBColorSpace
  tex.needsUpdate = true
  return tex
}

/** 拼接占位：12 格 × CELL_W，letterbox 至 CELL_H */
export function makePlaceholderStrip(band: BandId): CanvasTexture {
  const sources = config.filmstrip.frameSources
  const totalW = sources * CELL_W
  const c = document.createElement('canvas')
  c.width = totalW
  c.height = CELL_H
  const ctx = c.getContext('2d')!
  ctx.clearRect(0, 0, totalW, CELL_H)
  ctx.fillStyle = 'rgba(26, 22, 20, 0.55)'
  ctx.fillRect(0, 0, totalW, CELL_H)
  for (let i = 0; i < sources; i++) {
    const label = `${band.toUpperCase()}-${String(i + 1).padStart(2, '0')}`
    const cell = makePlaceholderTexture(label)
    const img = cell.image as HTMLCanvasElement
    ctx.drawImage(img, i * CELL_W, FRAME_CONTENT_Y, CELL_W, FRAME_CONTENT_H)
  }
  return configureStripTexture(new CanvasTexture(c))
}

function drawTextureLetterboxed(
  ctx: CanvasRenderingContext2D,
  img: CanvasImageSource,
  iw: number,
  ih: number,
  dx: number,
  dy: number,
  targetW: number,
  targetH: number,
) {
  const scale = Math.min(targetW / iw, targetH / ih)
  const dw = iw * scale
  const dh = ih * scale
  const ox = dx + (targetW - dw) / 2
  const oy = dy + (targetH - dh) / 2
  ctx.drawImage(img, ox, oy, dw, dh)
}

/** 12 帧拼成长条 CanvasTexture；可选整张 strip 预模糊（避免逐帧重复模糊） */
export async function loadBandStripTexture(
  band: BandId,
  blurPx: number,
): Promise<CanvasTexture> {
  const sources = config.filmstrip.frameSources
  const loader = new TextureLoader()
  const totalW = sources * CELL_W

  const results = await Promise.allSettled(
    Array.from({ length: sources }, (_, i) =>
      new Promise<Texture>((resolve, reject) =>
        loader.load(
          config.assets.paths.filmFrame(band, i),
          (tex) => {
            tex.colorSpace = SRGBColorSpace
            resolve(tex)
          },
          undefined,
          reject,
        ),
      ),
    ),
  )

  const work = document.createElement('canvas')
  work.width = totalW
  work.height = CELL_H
  const ctx = work.getContext('2d')!
  ctx.clearRect(0, 0, totalW, CELL_H)
  ctx.fillStyle = 'rgba(26, 22, 20, 0.55)'
  ctx.fillRect(0, 0, totalW, CELL_H)

  for (let i = 0; i < sources; i++) {
    const cx = i * CELL_W
    const r = results[i]
    if (r.status === 'fulfilled') {
      const tex = r.value
      const img = tex.image as HTMLImageElement | HTMLCanvasElement | ImageBitmap
      const iw = (img as HTMLImageElement).naturalWidth || (img as any).width || CELL_W
      const ih = (img as HTMLImageElement).naturalHeight || (img as any).height || FRAME_CONTENT_H
      drawTextureLetterboxed(ctx, img as CanvasImageSource, iw, ih, cx, FRAME_CONTENT_Y, CELL_W, FRAME_CONTENT_H)
    } else {
      const label = `${band.toUpperCase()}-${String(i + 1).padStart(2, '0')}`
      const cell = makePlaceholderTexture(label)
      ctx.drawImage(cell.image as HTMLCanvasElement, cx, FRAME_CONTENT_Y, CELL_W, FRAME_CONTENT_H)
    }
  }

  let canvas: HTMLCanvasElement = work
  if (blurPx > 0) {
    const blurC = document.createElement('canvas')
    blurC.width = totalW
    blurC.height = CELL_H
    const bctx = blurC.getContext('2d')!
    try {
      bctx.filter = `blur(${blurPx}px)`
    } catch {
      /* Safari old */
    }
    bctx.drawImage(work, 0, 0)
    canvas = blurC
  }

  return configureStripTexture(new CanvasTexture(canvas))
}
